import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import KeyboardDoubleArrowLeftRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import userIcon from "../../../static/media/icon/user.svg";
import briefcaseIcon from "../../../static/media/icon/briefcase.svg";

export default function TopBar({ sidebarOpen, onToggleSidebar }) {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const { user, logout } = useAuth();
  const { dashboardTheme, toggleThemeMode } = useDashboardTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const permissions = user?.permissions || [];

  const canViewEnterprise =
    user?.role === "super_admin" || permissions.includes("enterprise.view");

  const themeLabel =
    dashboardTheme.theme_mode === "dark" ? "Modo claro" : "Modo oscuro";

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const closeSession = () => {
    setAnchorEl(null);
    logout();
    navigate("/iniciar-sesion", { replace: true });
  };

  const goToCompany = () => {
    setAnchorEl(null);
    navigate("/admin-panel/empresa");
  };

  const handleToggleTheme = () => {
    toggleThemeMode();
    setAnchorEl(null);
  };

  return (
    <Box
      component="header"
      sx={{
        height: 76,
        bgcolor: dashboardTheme.card_background,
        borderBottom: `1px solid ${dashboardTheme.border_color}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: { xs: 2, md: 3 },
        position: "sticky",
        top: 0,
        zIndex: 20,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Tooltip title={sidebarOpen ? "Ocultar menú" : "Mostrar menú"}>
          <IconButton
            onClick={onToggleSidebar}
            sx={{
              width: 42,
              height: 42,
              borderRadius: "14px",
              color: dashboardTheme.text_color,
              "&:hover": {
                bgcolor: `${dashboardTheme.primary_color}18`,
              },
            }}
          >
            {sidebarOpen ? (
              <KeyboardDoubleArrowLeftRoundedIcon />
            ) : (
              <KeyboardDoubleArrowRightRoundedIcon />
            )}
          </IconButton>
        </Tooltip>

        <Button
          onClick={() => navigate("/admin-panel")}
          startIcon={<DashboardRoundedIcon />}
          sx={{
            height: 42,
            borderRadius: "14px",
            px: { xs: 1.1, sm: 1.4 },
            minWidth: { xs: 42, sm: "auto" },
            textTransform: "none",
            fontSize: 14,
            fontWeight: 600,
            color: dashboardTheme.text_color,
            boxShadow: "none",
            "& .MuiButton-startIcon": {
              color: dashboardTheme.primary_color,
              mr: { xs: 0, sm: 1 },
            },
            "&:hover": {
              bgcolor: `${dashboardTheme.primary_color}18`,
              boxShadow: "none",
            },
          }}
        >
          <Box
            component="span"
            sx={{
              display: { xs: "none", sm: "inline" },
            }}
          >
            Panel de Control
          </Box>
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        {!isMobile && canViewEnterprise && (
          <Tooltip title="Mi Compañía">
            <IconButton
              onClick={goToCompany}
              sx={{
                width: 42,
                height: 42,
                borderRadius: "14px",
                color: dashboardTheme.text_color,
                "&:hover": {
                  bgcolor: `${dashboardTheme.primary_color}18`,
                },
              }}
            >
              <Box
                component="img"
                src={briefcaseIcon}
                alt="Mi Compañía"
                sx={{
                  width: 22,
                  height: 22,
                  objectFit: "contain",
                  filter:
                    dashboardTheme.theme_mode === "dark"
                      ? "brightness(0) invert(1)"
                      : "none",
                }}
              />
            </IconButton>
          </Tooltip>
        )}

        {!isMobile && (
          <Tooltip title={themeLabel}>
            <IconButton
              onClick={toggleThemeMode}
              sx={{
                width: 42,
                height: 42,
                borderRadius: "14px",
                color: dashboardTheme.text_color,
                "&:hover": {
                  bgcolor: `${dashboardTheme.primary_color}18`,
                },
              }}
            >
              {dashboardTheme.theme_mode === "dark" ? (
                <LightModeRoundedIcon />
              ) : (
                <DarkModeRoundedIcon />
              )}
            </IconButton>
          </Tooltip>
        )}

        <Button
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            textTransform: "none",
            borderRadius: "16px",
            px: { xs: 0.8, sm: 1.2 },
            py: 0.8,
            color: dashboardTheme.text_color,
            boxShadow: "none",
            minWidth: 0,
            "&:hover": {
              bgcolor: `${dashboardTheme.primary_color}18`,
              boxShadow: "none",
            },
          }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              component="img"
              src={userIcon}
              alt="Usuario"
              sx={{
                width: 34,
                height: 34,
                objectFit: "contain",
                flexShrink: 0,
                filter:
                  dashboardTheme.theme_mode === "dark"
                    ? "brightness(0) invert(1)"
                    : "none",
              }}
            />

            {!isMobile && (
              <Box sx={{ textAlign: "left", maxWidth: { sm: 220, md: 260 }, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: dashboardTheme.text_color,
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user?.name || "Usuario"}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: dashboardTheme.muted_text_color,
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user?.email || "Sin correo"}
                </Typography>
              </Box>
            )}

            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: 20,
                color: dashboardTheme.muted_text_color,
                flexShrink: 0,
              }}
            />
          </Stack>
        </Button>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        disableScrollLock
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 230,
            borderRadius: "16px",
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow: "0 18px 45px rgba(17,24,39,0.12)",
            bgcolor: dashboardTheme.card_background,
          },
        }}
      >
        {isMobile && (
          <MenuItem
            onClick={handleToggleTheme}
            sx={{
              gap: 1.2,
              fontSize: 14,
              fontWeight: 500,
              color: dashboardTheme.text_color,
              py: 1.2,
              "&:hover": {
                bgcolor: `${dashboardTheme.primary_color}18`,
              },
            }}
          >
            {dashboardTheme.theme_mode === "dark" ? (
              <LightModeRoundedIcon
                sx={{ fontSize: 21, color: dashboardTheme.muted_text_color }}
              />
            ) : (
              <DarkModeRoundedIcon
                sx={{ fontSize: 21, color: dashboardTheme.muted_text_color }}
              />
            )}
            {themeLabel}
          </MenuItem>
        )}

        {isMobile && canViewEnterprise && (
          <MenuItem
            onClick={goToCompany}
            sx={{
              gap: 1.2,
              fontSize: 14,
              fontWeight: 500,
              color: dashboardTheme.text_color,
              py: 1.2,
              "&:hover": {
                bgcolor: `${dashboardTheme.primary_color}18`,
              },
            }}
          >
            <Box
              component="img"
              src={briefcaseIcon}
              alt="Mi Compañía"
              sx={{
                width: 21,
                height: 21,
                objectFit: "contain",
                filter:
                  dashboardTheme.theme_mode === "dark"
                    ? "brightness(0) invert(1)"
                    : "none",
              }}
            />
            Mi Compañía
          </MenuItem>
        )}

        <MenuItem
          onClick={closeSession}
          sx={{
            gap: 1.2,
            fontSize: 14,
            fontWeight: 500,
            color: dashboardTheme.text_color,
            py: 1.2,
            "&:hover": {
              bgcolor: `${dashboardTheme.primary_color}18`,
            },
          }}
        >
          <LogoutRoundedIcon
            sx={{ fontSize: 21, color: dashboardTheme.muted_text_color }}
          />
          Cerrar sesión
        </MenuItem>
      </Menu>
    </Box>
  );
}